import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    CartesianGrid,
    ResponsiveContainer,
} from "recharts";
import { theme } from "../theme";
import SkeletonCard from "./common/SkeletonCard";
import EmptyState from "./common/EmptyState";
import DashboardCard from "./common/DashboardCard";


const monthLabels = [
    "JANUARY", "FEBRUARY", "MARCH", "APRIL", "MAY", "JUNE",
    "JULY", "AUGUST", "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER"
];

function YearComparison({ filterContext, bundleData, onMaximize, fullScreen = false }) {
    const { salesperson, region, displayCurrency = "AED" } = filterContext || {};
    const currentYear = useMemo(() => new Date().getFullYear(), []);
    const [data, setData] = useState([]);
    const [yearsToCompare, setYearsToCompare] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        const main = currentYear;
        const compare = [main - 2, main - 1, main].filter((y) => y > 0);
        setYearsToCompare(compare);
    }, [currentYear]);

    useEffect(() => {
        if (bundleData) {
            try {
                const bundleYears = bundleData.context?.years || yearsToCompare;
                const bundleRows = bundleData.data || [];
                const base = monthLabels.map((name, idx) => ({ monthIndex: idx + 1, month: name }));
                const merged = base.map((m) => {
                    const out = { ...m };
                    bundleYears.forEach((y) => {
                        const yearData = bundleRows.find((r) => r.year === y);
                        const monthValue = yearData?.months?.[m.monthIndex] || 0;
                        out[`y${y}`] = Number(monthValue);
                    });
                    return out;
                });
                setData(merged);
                setLoading(false);
                setError("");
            } catch (err) {
                console.error("Error processing bundle data:", err);
                setError("Failed to process year comparison data");
            }
            return;
        }
        if (!salesperson || yearsToCompare.length === 0) {
            setData([]);
            setLoading(false);
            return;
        }

        if (!bundleData) {
            setLoading(true);
            setError("");
            return;
        }
    }, [salesperson, region, yearsToCompare, bundleData]);

    const hasData = useMemo(() => (
        data.length > 0 && data.some((d) => yearsToCompare.some((y) => d[`y${y}`] > 0))
    ), [data, yearsToCompare]);

    const yearTotals = useMemo(() => {
        const totals = {};
        yearsToCompare.forEach((y) => {
            totals[y] = data.reduce((sum, d) => sum + (d[`y${y}`] || 0), 0);
        });
        return totals;
    }, [data, yearsToCompare]);

    const totalAllYears = useMemo(() => (
        data.reduce((sum, d) => sum + yearsToCompare.reduce((ys, y) => ys + (d[`y${y}`] || 0), 0), 0)
    ), [data, yearsToCompare]);

    const CustomTooltip = useCallback(({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div style={{
                    backgroundColor: "rgba(15, 23, 42, 0.95)",
                    backdropFilter: "blur(12px)",
                    border: "1px solid rgba(255,255,255,0.2)",
                    borderRadius: 12,
                    padding: "16px",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.6)"
                }}>
                    <p style={{ margin: "0 0 12px 0", color: "#ffffff", fontSize: 16, fontWeight: 800, borderBottom: "1px solid rgba(255,255,255,0.1)", paddingBottom: 8 }}>{label}</p>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {payload.map((entry, index) => (
                            <p key={index} style={{ margin: 0, color: entry.color, fontSize: 14, fontWeight: 700, display: "flex", justifyContent: "space-between", gap: 20 }}>
                                <span>{entry.name}:</span>
                                <span style={{ color: "#fff", fontSize: 16 }}>{displayCurrency} {Math.round(Number(entry.value)).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                            </p>
                        ))}
                    </div>
                </div>
            );
        }
        return null;
    }, [displayCurrency]);

    const actionButtons = useMemo(() => (
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            {onMaximize && !fullScreen && (
                <button
                    onClick={onMaximize}
                    style={{
                        padding: "8px",
                        background: "rgba(255, 255, 255, 0.05)",
                        color: "#e5e7eb",
                        borderRadius: 8,
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.2s ease",
                    }}
                    title="Maximize"
                >
                    <span style={{ fontSize: "16px", lineHeight: 1 }}>⤢</span>
                </button>
            )}
            {fullScreen && onMaximize && (
                <button
                    onClick={onMaximize}
                    style={{
                        padding: "8px",
                        background: "rgba(255, 107, 53, 0.2)",
                        color: "#ff6b35",
                        border: "1px solid #ff6b35",
                        borderRadius: "50%",
                        width: "36px",
                        height: "36px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.2s ease",
                    }}
                >

                </button>
            )}
        </div>
    ), [onMaximize, fullScreen]);

    const Subtitle = () => (
        <p style={{
            color: "#f8fafc",
            fontSize: fullScreen ? "20px" : "14px",
            marginTop: fullScreen ? "60px" : "-10px",
            marginBottom: theme.spacing.lg,
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
            fontWeight: 700,
            letterSpacing: "0.5px",
            textAlign: fullScreen ? "center" : "left",
            textShadow: "0 2px 4px rgba(0,0,0,0.5)"
        }}>
            {salesperson && <span style={{ color: "#ff8a3d" }}>{salesperson}</span>} {/* Orange for name */}
            {region && <span style={{ color: "#cbd5e1", fontWeight: 500 }}> · {region === "all" ? "All Regions" : region}</span>}
        </p>
    );

    const ChartArea = () => (
        <div style={{ width: "100%", height: fullScreen ? "600px" : "350px", minHeight: "350px", flex: "none", marginBottom: fullScreen ? "40px" : "0", background: "rgba(15, 20, 35, 0.4)", borderRadius: 12 }}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: fullScreen ? 20 : 10, right: fullScreen ? 50 : 30, left: fullScreen ? 40 : 10, bottom: fullScreen ? 50 : 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
                    <XAxis dataKey="month" stroke="#ffffff" tick={{ fill: "#ffffff", fontSize: fullScreen ? 14 : 11, fontWeight: 600 }} angle={-30} textAnchor="end" height={fullScreen ? 80 : 60} interval={0} tickFormatter={(v) => v.substring(0, 3)} axisLine={{ stroke: "rgba(255,255,255,0.4)" }} />
                    <YAxis stroke="#ffffff" tick={{ fill: "#ffffff", fontSize: fullScreen ? 14 : 11, fontWeight: 600 }} tickFormatter={(v) => `${(v / 1_000_000).toFixed(1)}M`} width={fullScreen ? 70 : 50} axisLine={{ stroke: "rgba(255,255,255,0.4)" }} />
                    <Tooltip
                        content={<CustomTooltip />}
                    />
                    <Legend wrapperStyle={{ paddingTop: "20px" }} iconType="circle" iconSize={14} formatter={(value) => <span style={{ color: "#f1f5f9", fontSize: 14, fontWeight: 600 }}>{value}</span>} />
                    {yearsToCompare.map((y, idx) => {
                        const yearColors = ["#ff6b35", "#3b82f6", "#10b981"];
                        const lineColor = yearColors[idx % yearColors.length];
                        return (
                            <Line
                                key={y}
                                type="monotone"
                                dataKey={`y${y}`}
                                name={String(y)}
                                stroke={lineColor}
                                strokeWidth={6}
                                dot={{ r: 6, strokeWidth: 2, stroke: "#0f172a", fill: lineColor }}
                                activeDot={{ r: 9, fill: lineColor, stroke: "rgba(255,255,255,0.4)", strokeWidth: 8 }}
                                animationDuration={1000}
                            />
                        );
                    })}
                </LineChart>
            </ResponsiveContainer>
        </div>
    );

    const SummaryItems = () => (
        <div style={{ marginTop: "18px", marginBottom: "40px", display: "flex", gap: 20, flexWrap: "wrap", alignItems: "center", justifyContent: fullScreen ? "center" : "flex-start" }}>
            <div style={{
                padding: fullScreen ? "16px 24px" : "12px 20px",
                background: "linear-gradient(135deg, rgba(255, 107, 53, 0.1) 0%, rgba(255, 107, 53, 0.02) 100%)",
                borderRadius: "12px",
                border: "1px solid rgba(255, 107, 53, 0.2)",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                minWidth: "140px"
            }}>
                <span style={{ color: "#ff6b35", fontSize: "11px", textTransform: "uppercase", letterSpacing: "1px", fontWeight: "600" }}>
                    Total ({yearsToCompare[0]}-{yearsToCompare[yearsToCompare.length - 1]})
                </span>
                <span style={{
                    color: "#fff",
                    fontSize: fullScreen ? "24px" : "18px",
                    fontWeight: "800",
                    marginTop: "4px"
                }}>
                    {displayCurrency} {Math.round(totalAllYears).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </span>
            </div>
            {yearsToCompare.map((y, idx) => {
                const yearTotal = yearTotals[y] || 0;
                const yearColors = ["#ff6b35", "#3b82f6", "#10b981"];
                const yearColor = yearColors[idx % yearColors.length];
                const bgColors = [
                    "rgba(255, 107, 53, 0.1)",
                    "rgba(59, 130, 246, 0.1)",
                    "rgba(16, 185, 129, 0.1)"
                ];
                const bgColor = bgColors[idx % bgColors.length];
                return (
                    <div key={y} style={{
                        padding: fullScreen ? "16px 24px" : "12px 20px",
                        background: `linear-gradient(135deg, ${bgColor} 0%, rgba(255,255,255,0) 100%)`,
                        borderRadius: "12px",
                        border: `1px solid ${yearColor}40`,
                        display: "flex",
                        flexDirection: "column",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        minWidth: "120px"
                    }}>
                        <span style={{ color: yearColor, fontSize: "11px", textTransform: "uppercase", letterSpacing: "1px", fontWeight: "600" }}>{y}</span>
                        <span style={{
                            color: "#fff",
                            fontSize: fullScreen ? "24px" : "18px",
                            fontWeight: "800",
                            marginTop: "4px"
                        }}>
                            {displayCurrency} {Math.round(yearTotal).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </span>
                    </div>
                );
            })}
        </div>
    );

    if (!salesperson) return null;

    return (
        <DashboardCard
            title="Year Wise Comparison"
            actions={actionButtons}
            fullScreen={fullScreen}
            gradient="primary"
        >
            {loading && <SkeletonCard height="400px" />}
            {error && (
                <div style={{ padding: theme.spacing.lg, background: "rgba(239, 68, 68, 0.1)", borderRadius: theme.borderRadius.md, border: `1px solid ${theme.colors.danger}`, color: "#fca5a5" }}>
                    <strong>Error:</strong> {error}
                </div>
            )}
            {!loading && !error && !hasData && (
                <EmptyState icon="" message="No data found for the selected years." size="medium" />
            )}
            {!loading && !error && hasData && (
                <div style={{
                    height: "100%",
                    overflowY: fullScreen ? "auto" : "visible",
                    paddingRight: fullScreen ? "8px" : "0",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: fullScreen ? "center" : "flex-start",
                    alignItems: "stretch"
                }}>
                    <Subtitle />
                    <SummaryItems />
                    <ChartArea />
                </div>
            )}
        </DashboardCard>
    );
}

export default YearComparison;
